import React from 'react';
import { requestDroneFocus } from '../../utils/voiceLiveBus';

/**
 * TelemetryPill — bottom-center network/telemetry indicator from the
 * Stitch design: link status + HDG / SPD / GPS for the tracked drone.
 */

function Stat({ value, label, color = 'var(--accent)' }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 44 }}>
            <span className="stitch-label" style={{ fontSize: 12, fontWeight: 600, color }}>{value}</span>
            <span className="stitch-label" style={{ fontSize: 8, color: 'var(--stitch-outline)' }}>{label}</span>
        </div>
    );
}

function TelemetryPill({ telemetry = {}, connected = false, droneId = null, droneName = '', droneType = '', selected = false, onFocus = null }) {
    const heading = Number.isFinite(parseFloat(telemetry.heading)) ? `${Math.round(parseFloat(telemetry.heading))}°` : '—';
    const speed = Number.isFinite(parseFloat(telemetry.groundspeed)) ? `${parseFloat(telemetry.groundspeed).toFixed(1)}m/s` : '—';
    const sats = Number.isFinite(parseInt(telemetry.satellites)) ? (parseInt(telemetry.satellites) >= 10 ? '10+' : `${parseInt(telemetry.satellites)}`) : '—';
    const battery = Number.isFinite(parseFloat(telemetry.battery_remaining)) ? `${Math.round(parseFloat(telemetry.battery_remaining))}%` : '—';
    const altitude = `${(parseFloat(telemetry.relative_alt ?? telemetry.alt) || 0).toFixed(1)}m`;
    const lat = (parseFloat(telemetry.lat) || 0).toFixed(4);
    const lon = (parseFloat(telemetry.lon) || 0).toFixed(4);

    const batRaw = parseFloat(telemetry.battery_remaining);
    const batColor = Number.isFinite(batRaw)
        ? (batRaw > 40 ? 'var(--success)' : batRaw > 20 ? 'var(--warning)' : 'var(--danger)')
        : 'var(--accent)';

    if (selected) {
        return (
            <div
                className="glass-panel active-glow"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    padding: '8px 24px',
                    borderRadius: 999,
                    border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                    boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
                    pointerEvents: 'auto',
                    whiteSpace: 'nowrap',
                }}
            >
                {/* 1) Network & Identity status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} title={`Tracking ${droneName}`}>
                    <span
                        className="material-symbols-outlined"
                        style={{ fontSize: 18, color: connected ? 'var(--accent)' : 'var(--textMuted)' }}
                    >
                        hub
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 64 }}>
                        <span className="stitch-label" style={{ fontSize: 11, fontWeight: 700, color: 'var(--textPrimary)', margin: 0, textTransform: 'uppercase' }}>
                            {droneName}
                        </span>
                        <span className="stitch-label" style={{ fontSize: 8, color: 'var(--stitch-outline)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {droneType || 'PX4'}
                        </span>
                    </div>
                    <span
                        className={connected ? 'stitch-pulse' : undefined}
                        style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? 'var(--success)' : 'var(--textMuted)' }}
                    />
                </div>

                <div style={{ width: 1, height: 20, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />

                {/* 2) Battery & Altitude */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Stat value={battery} label="BATTERY" color={batColor} />
                    <Stat value={altitude} label="ALTITUDE" />
                </div>

                <div style={{ width: 1, height: 20, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />

                {/* 3) Navigation Stats */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Stat value={heading} label="HDG" />
                    <Stat value={speed} label="SPD" />
                    <Stat value={sats} label="GPS" />
                </div>

                <div style={{ width: 1, height: 20, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />

                {/* 4) Position (Latitude / Longitude) */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
                    <span className="stitch-label" style={{ fontSize: 10, fontWeight: 600, color: 'var(--textPrimary)', lineHeight: 1.2 }}>{lat}</span>
                    <span className="stitch-label" style={{ fontSize: 10, fontWeight: 600, color: 'var(--textPrimary)', lineHeight: 1.2 }}>{lon}</span>
                    <span className="stitch-label" style={{ fontSize: 8, color: 'var(--stitch-outline)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>POS (LAT/LON)</span>
                </div>

                <div style={{ width: 1, height: 20, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />

                {/* 5) Focus on Drone button */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onFocus) onFocus();
                        else if (droneId) requestDroneFocus(droneId);
                    }}
                    title={droneName ? `Focus on ${droneName}` : 'Focus on Drone'}
                    aria-label="Focus on Drone"
                    style={{
                        background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
                        color: 'var(--accent)',
                        borderRadius: '50%',
                        width: 30,
                        height: 30,
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        boxShadow: '0 0 10px color-mix(in srgb, var(--accent) 20%, transparent)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--accent)';
                        e.currentTarget.style.color = '#00363d';
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 0 16px color-mix(in srgb, var(--accent) 60%, transparent)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 12%, transparent)';
                        e.currentTarget.style.color = 'var(--accent)';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 0 10px color-mix(in srgb, var(--accent) 20%, transparent)';
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                        helicopter
                    </span>
                </button>
            </div>
        );
    }

    return (
        <div
            className="glass-panel"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                padding: '8px 24px',
                borderRadius: 999,
                border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
                pointerEvents: 'auto',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} title={droneName ? `Tracking ${droneName}` : 'Network link'}>
                <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 18, color: connected ? 'var(--accent)' : 'var(--textMuted)' }}
                >
                    hub
                </span>
                <span className="stitch-label" style={{ fontSize: 12, color: 'var(--textPrimary)' }}>
                    {connected ? 'NID CONNECTED' : 'NID OFFLINE'}
                </span>
                <span
                    className={connected ? 'stitch-pulse' : undefined}
                    style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? 'var(--success)' : 'var(--textMuted)' }}
                />
            </div>
            <div style={{ width: 1, height: 16, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Stat value={heading} label="HDG" />
                <Stat value={speed} label="SPD" />
                <Stat value={sats} label="GPS" />
            </div>
            {droneId && (
                <>
                    <div style={{ width: 1, height: 16, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onFocus) onFocus();
                            else if (droneId) requestDroneFocus(droneId);
                        }}
                        title={droneName ? `Focus on ${droneName}` : 'Focus on Drone'}
                        aria-label="Focus on Drone"
                        style={{
                            background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
                            color: 'var(--accent)',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxSizing: 'border-box',
                            flexShrink: 0,
                        }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                            helicopter
                        </span>
                    </button>
                </>
            )}
        </div>
    );
}

export default TelemetryPill;
